import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, RefreshCw, Loader2, Plus, Phone, Send, Eye, ClipboardCopy, Check, MessageSquare, ExternalLink, Bike, AlertTriangle, Download, Archive } from "lucide-react";

const MEETING_POINTS = [
  "Meeting Point A - The Weir",
  "Meeting Point B - Opp. Speaker Connolly",
  "Coffee@The Edge",
];

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    confirmed: "bg-green-100 text-green-800 border-green-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

function toLocalDatetime(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const BLANK_NEW = {
  requester_name: "", requester_email: "", requester_phone: "",
  booking_datetime_iso: "", duration_hours: 1, status: "confirmed",
  meeting_point: "",
  bike_choice: "Trishaw Only",
  time_flexibility: "",
  volunteer_id: "", volunteer_name: "", volunteer_email: "",
  trishaw_volunteer_id: "", trishaw_volunteer_name: "", trishaw_volunteer_email: "", trishaw_volunteer_phone: "",
  wheelchair_volunteer_id: "", wheelchair_volunteer_name: "", wheelchair_volunteer_email: "", wheelchair_volunteer_phone: "",
  send_confirmation: false, send_notifications: false,
};

// Returns the current local datetime formatted for a datetime-local input min attribute
function localNow() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Returns the conflicting booking or null — mirrors backend gap_minutes=20 logic
function findConflict(proposedIso, durationHours, bookings) {
  if (!proposedIso) return null;
  const GAP_MS = 20 * 60 * 1000;
  const dur = parseFloat(durationHours) || 1;
  const pStart = new Date(proposedIso).getTime();
  const pEnd   = pStart + dur * 3600000 + GAP_MS;
  for (const b of bookings) {
    if (!["pending", "confirmed"].includes(b.status)) continue;
    const bStart = new Date(b.booking_datetime_iso).getTime();
    const bEnd   = bStart + (b.duration_hours || 1) * 3600000 + GAP_MS;
    if (pEnd > bStart && bEnd > pStart) return b;
  }
  return null;
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  // Edit state
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({ booking_datetime_iso: "", duration_hours: 1, status: "pending", meeting_point: "", requester_name: "", requester_email: "", requester_phone: "", volunteer_phone: "" });
  const [saving, setSaving] = useState(false);

  // View state
  const [viewBooking, setViewBooking] = useState(null);
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [cancellingBike, setCancellingBike] = useState(null); // "trishaw" | "wheelchair" | null

  const handleCancelBike = async (bookingId, bike) => {
    const label = bike === "trishaw" ? "Trishaw" : "Wheelchair Bike";
    if (!window.confirm(`Cancel the ${label} slot for this booking? Emails will be sent to the volunteer and requester.`)) return;
    setCancellingBike(bike);
    try {
      await api.cancelBike(bookingId, bike);
      toast.success(`${label} slot cancelled`);
      await fetchBookings();
      // refresh view booking
      if (viewBooking?.id === bookingId) {
        const updated = (await api.getBookings()).data.bookings.find((b) => b.id === bookingId);
        if (updated) setViewBooking(updated);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to cancel ${label} slot`);
    } finally {
      setCancellingBike(null);
    }
  };

  const buildWhatsAppSnippet = (b) => {
    const lines = [
      `🚨 Heads up. Emails sent for a new booking request`,
      ``,
      `Date & Time: ${b.booking_dt_formatted || "—"}`,
      `Meeting Point: ${b.meeting_point || "—"}`,
    ];
    if (b.is_group_booking && b.bike_choice) {
      lines.push(`Bike Choice: ${b.bike_choice}`);
    } else {
      lines.push(`Time Flexibility: ${b.time_flexibility || "—"}`);
    }
    return lines.join("\n");
  };

  const handleCopySnippet = (b) => {
    navigator.clipboard.writeText(buildWhatsAppSnippet(b)).then(() => {
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 2000);
    });
  };

  // New booking state
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState(BLANK_NEW);
  const [creating, setCreating] = useState(false);

  const [deleting, setDeleting] = useState(null);
  const [resending, setResending] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [archiving, setArchiving] = useState(false);

  const fetchBookings = useCallback(() => {
    setLoading(true);
    api.getAdminBookings()
      .then((r) => setBookings(r.data.bookings))
      .catch(() => toast.error("Failed to load bookings"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchBookings();
    api.getVolunteers().then((r) => setVolunteers(r.data.volunteers)).catch(() => { /* volunteers list is supplementary */ });
  }, [fetchBookings]);

  const TWO_HOURS = 2 * 60 * 60 * 1000;
  const now = Date.now();

  const isCompleted = (b) => {
    if (b.status !== "confirmed") return false;
    const bdt = new Date(b.booking_datetime_iso).getTime();
    return (now - bdt) > TWO_HOURS;
  };

  const filtered = (() => {
    if (filter === "all") return bookings;
    if (filter === "confirmed") return bookings.filter((b) => b.status === "confirmed" && !isCompleted(b));
    if (filter === "completed") return bookings.filter((b) => isCompleted(b));
    return bookings.filter((b) => b.status === filter);
  })();

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((b) => b.id)));
    }
  };

  const handleArchive = async () => {
    if (selected.size === 0) return;
    setArchiving(true);
    try {
      await api.archiveBookings([...selected]);
      toast.success(`${selected.size} booking(s) archived`);
      setSelected(new Set());
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive bookings");
    } finally {
      setArchiving(false);
    }
  };

  // --- Edit ---
  const openEdit = (b) => {
    setEditBooking(b);
    const tv = b.trishaw_volunteer || {};
    const wv = b.wheelchair_volunteer || {};
    setEditForm({
      booking_datetime_iso: toLocalDatetime(b.booking_datetime_iso),
      duration_hours: b.duration_hours,
      status: b.status,
      volunteer_name: b.claimed_by_name || "",
      volunteer_email: b.claimed_by_email || "",
      volunteer_phone: b.claimed_by_phone || "",
      meeting_point: b.meeting_point || "",
      requester_name: b.requester_name || "",
      requester_email: b.requester_email || "",
      requester_phone: b.requester_phone || "",
      trishaw_volunteer_name: tv.name || "",
      trishaw_volunteer_email: tv.email || "",
      trishaw_volunteer_phone: tv.phone || "",
      wheelchair_volunteer_name: wv.name || "",
      wheelchair_volunteer_email: wv.email || "",
      wheelchair_volunteer_phone: wv.phone || "",
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const isDual = editBooking.bike_choice === "Trishaw and Wheelchair Bike";
      const trishawCleared  = isDual && editForm.trishaw_volunteer_name     === "";
      const wheelchairCleared = isDual && editForm.wheelchair_volunteer_name === "";

      // Only include booking_datetime_iso if it actually changed
      const newDatetimeISO = new Date(editForm.booking_datetime_iso).toISOString();
      const datetimeChanged = newDatetimeISO !== new Date(editBooking.booking_datetime_iso).toISOString();

      const payload = {
        ...(datetimeChanged && { booking_datetime_iso: newDatetimeISO }),
        duration_hours: parseFloat(editForm.duration_hours),
        status: editForm.status,
        volunteer_name: isDual ? null : (editForm.volunteer_name || null),
        volunteer_email: isDual ? null : (editForm.volunteer_email || null),
        volunteer_phone: isDual ? null : (editForm.volunteer_phone || null),
        meeting_point: editForm.meeting_point || null,
        requester_name: editForm.requester_name || null,
        requester_email: editForm.requester_email || null,
        requester_phone: editForm.requester_phone || null,
        // Trishaw slot
        ...(isDual && trishawCleared
          ? { clear_trishaw_volunteer: true }
          : {
              trishaw_volunteer_name:  isDual ? (editForm.trishaw_volunteer_name  || null) : null,
              trishaw_volunteer_email: isDual ? (editForm.trishaw_volunteer_email || null) : null,
              trishaw_volunteer_phone: isDual ? (editForm.trishaw_volunteer_phone || null) : null,
            }),
        // Wheelchair slot
        ...(isDual && wheelchairCleared
          ? { clear_wheelchair_volunteer: true }
          : {
              wheelchair_volunteer_name:  isDual ? (editForm.wheelchair_volunteer_name  || null) : null,
              wheelchair_volunteer_email: isDual ? (editForm.wheelchair_volunteer_email || null) : null,
              wheelchair_volunteer_phone: isDual ? (editForm.wheelchair_volunteer_phone || null) : null,
            }),
      };
      await api.updateBooking(editBooking.id, payload);
      toast.success("Booking updated");
      setEditBooking(null);
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update booking");
    } finally {
      setSaving(false);
    }
  };

  // --- Resend volunteer notifications ---
  const handleResend = async (id) => {
    setResending(id);
    try {
      await api.resendVolunteerNotifications(id);
      toast.success("Volunteer notifications resent");
      // Update local state to show reminder was sent
      setBookings((prev) => prev.map((b) => 
        b.id === id 
          ? { 
              ...b, 
              volunteer_reminder_sent_at: new Date().toISOString(),
              volunteer_reminder_count: (b.volunteer_reminder_count || 0) + 1
            } 
          : b
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend notifications");
    } finally {
      setResending(null);
    }
  };

  // --- Cancel / Delete ---
  const [bikeDeleteModal, setBikeDeleteModal] = useState(null); // { booking }
  const [bikeCancelling, setBikeCancelling] = useState(null);   // "trishaw" | "wheelchair" | "both" | null
  const [cancelModal, setCancelModal] = useState(null); // { booking }
  const [cancelReason, setCancelReason] = useState("");
  const [sendCancelNotifications, setSendCancelNotifications] = useState(true);

  const handleDelete = (b) => {
    if (b.bike_choice === "Trishaw and Wheelchair Bike") {
      setBikeDeleteModal({ booking: b });
    } else {
      // Show cancel modal for regular bookings
      setCancelModal({ booking: b });
      setCancelReason("");
      setSendCancelNotifications(true);
    }
  };

  const doCancelBooking = async () => {
    if (!cancelModal) return;
    const id = cancelModal.booking.id;
    setDeleting(id);
    try {
      await api.cancelBooking(id, cancelReason || null, sendCancelNotifications);
      toast.success("Booking cancelled");
      await fetchBookings(); // Refresh to show in Cancelled tab
      setCancelModal(null);
      setCancelReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel booking");
    } finally {
      setDeleting(null);
    }
  };

  const doDeleteBooking = async (id) => {
    setDeleting(id);
    try {
      await api.deleteBooking(id);
      toast.success("Booking deleted permanently");
      setBookings((prev) => prev.filter((b) => b.id !== id));
      setBikeDeleteModal(null);
    } catch {
      toast.error("Failed to delete booking");
    } finally {
      setDeleting(null);
    }
  };

  const handlePermanentDelete = (b) => {
    if (!window.confirm(`Permanently delete this cancelled booking for "${b.requester_name}"?\n\nThis will remove it from all statistics and cannot be undone.`)) return;
    doDeleteBooking(b.id);
  };

  const handleBikeCancel = async (bookingId, bike) => {
    setBikeCancelling(bike);
    try {
      if (bike === "both") {
        const bk = bikeDeleteModal?.booking;
        const bothWereCancelled =
          (bk?.trishaw_volunteer?.status) === "cancelled" &&
          (bk?.wheelchair_volunteer?.status) === "cancelled";
        await api.deleteBooking(bookingId);
        toast.success(bothWereCancelled ? "Booking deleted" : "Booking deleted — both volunteers notified");
        setBookings((prev) => prev.filter((b) => b.id !== bookingId));
      } else {
        await api.cancelBike(bookingId, bike);
        const label = bike === "trishaw" ? "Trishaw" : "Wheelchair Bike";
        toast.success(`${label} slot cancelled`);
        await fetchBookings();
      }
      setBikeDeleteModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    } finally {
      setBikeCancelling(null);
    }
  };

  // --- New Booking ---
  const handleVolunteerSelect = (volunteerId) => {
    if (volunteerId === "__none__") {
      setNewForm({ ...newForm, volunteer_id: "", volunteer_name: "", volunteer_email: "" });
      return;
    }
    const vol = volunteers.find((v) => v.id === volunteerId);
    if (vol) {
      setNewForm({ ...newForm, volunteer_id: volunteerId, volunteer_name: vol.name, volunteer_email: vol.email });
    }
  };

  const handleDualVolunteerSelect = (bike, volunteerId) => {
    const clear = { [`${bike}_volunteer_id`]: "", [`${bike}_volunteer_name`]: "", [`${bike}_volunteer_email`]: "", [`${bike}_volunteer_phone`]: "" };
    if (volunteerId === "__none__") {
      setNewForm({ ...newForm, ...clear });
      return;
    }
    const vol = volunteers.find((v) => v.id === volunteerId);
    if (vol) {
      setNewForm({
        ...newForm,
        [`${bike}_volunteer_id`]: volunteerId,
        [`${bike}_volunteer_name`]: vol.name,
        [`${bike}_volunteer_email`]: vol.email,
        [`${bike}_volunteer_phone`]: vol.phone || "",
      });
    }
  };

  const handleBikeChoiceChange = (v) => {
    // When switching bike type, clear any volunteer assignments so we don't leak
    // single-bike volunteer state into a dual booking (or vice versa).
    setNewForm({
      ...newForm,
      bike_choice: v,
      volunteer_id: "", volunteer_name: "", volunteer_email: "",
      trishaw_volunteer_id: "", trishaw_volunteer_name: "", trishaw_volunteer_email: "", trishaw_volunteer_phone: "",
      wheelchair_volunteer_id: "", wheelchair_volunteer_name: "", wheelchair_volunteer_email: "", wheelchair_volunteer_phone: "",
    });
  };

  const handleEditVolunteerSelect = (volunteerId) => {
    if (volunteerId === "__none__") {
      setEditForm({ ...editForm, volunteer_name: "", volunteer_email: "", volunteer_phone: "" });
      return;
    }
    const vol = volunteers.find((v) => v.id === volunteerId);
    if (vol) setEditForm({ ...editForm, volunteer_name: vol.name, volunteer_email: vol.email, volunteer_phone: vol.phone || "" });
  };

  const handleCreate = async () => {
    if (!newForm.requester_name || !newForm.requester_email || !newForm.booking_datetime_iso) {
      toast.error("Please fill in all required fields");
      return;
    }
    setCreating(true);
    try {
      const isDualNew = newForm.bike_choice === "Trishaw and Wheelchair Bike";
      await api.adminCreateBooking({
        requester_name: newForm.requester_name,
        requester_email: newForm.requester_email,
        requester_phone: newForm.requester_phone,
        booking_datetime_iso: new Date(newForm.booking_datetime_iso).toISOString(),
        duration_hours: parseFloat(newForm.duration_hours),
        status: newForm.status,
        meeting_point: newForm.meeting_point || null,
        bike_choice: newForm.bike_choice || "Trishaw Only",
        time_flexibility: newForm.time_flexibility || null,
        volunteer_name: isDualNew ? null : (newForm.volunteer_name || null),
        volunteer_email: isDualNew ? null : (newForm.volunteer_email || null),
        trishaw_volunteer_name:    isDualNew ? (newForm.trishaw_volunteer_name    || null) : null,
        trishaw_volunteer_email:   isDualNew ? (newForm.trishaw_volunteer_email   || null) : null,
        trishaw_volunteer_phone:   isDualNew ? (newForm.trishaw_volunteer_phone   || null) : null,
        wheelchair_volunteer_name:  isDualNew ? (newForm.wheelchair_volunteer_name  || null) : null,
        wheelchair_volunteer_email: isDualNew ? (newForm.wheelchair_volunteer_email || null) : null,
        wheelchair_volunteer_phone: isDualNew ? (newForm.wheelchair_volunteer_phone || null) : null,
        send_confirmation: newForm.send_confirmation,
        send_notifications: newForm.send_notifications,
      });
      toast.success("Booking created successfully");
      setShowNew(false);
      setNewForm(BLANK_NEW);
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create booking");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Bookings</h1>
          <p className="text-slate-500 mt-1">Manage all accessible bike bookings</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button
              variant="outline"
              onClick={handleArchive}
              disabled={archiving}
              className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="archive-bookings-btn"
            >
              {archiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
              Archive ({selected.size})
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => api.exportBookingsCSV(filter).then(r => { const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "bookings.csv"; a.click(); URL.revokeObjectURL(url); })} 
            className="gap-2 border-slate-200 text-slate-600 hover:bg-slate-50" 
            data-testid="export-bookings-btn"
            title="Export to CSV"
          >
            <Download className="w-4 h-4" /> Export
          </Button>
          <Button variant="outline" onClick={fetchBookings} className="gap-2 border-green-200 text-green-700 hover:bg-green-50" data-testid="refresh-bookings-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowNew(true)} data-testid="new-booking-btn" className="gap-2 bg-[#15803d] hover:bg-green-800 text-white rounded-full px-5">
            <Plus className="w-4 h-4" /> New Booking
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5 flex-wrap" data-testid="booking-filters">
        {["pending", "confirmed", "completed", "cancelled", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filter === f ? "bg-[#15803d] text-white shadow-sm" : "bg-white border border-green-200 text-slate-600 hover:bg-green-50"
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {` (${
              f === "all" ? bookings.length :
              f === "confirmed" ? bookings.filter((b) => b.status === "confirmed" && !isCompleted(b)).length :
              f === "completed" ? bookings.filter((b) => isCompleted(b)).length :
              bookings.filter((b) => b.status === f).length
            })`}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden" data-testid="bookings-table">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading bookings...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">No {filter !== "all" ? filter : ""} bookings found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" className="w-4 h-4 accent-green-700"
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onChange={toggleSelectAll}
                      data-testid="select-all-bookings" />
                  </th>
                  <th className="text-left px-5 py-3 font-semibold">Requester</th>
                  <th className="text-left px-5 py-3 font-semibold">Contact</th>
                  <th className="text-left px-5 py-3 font-semibold">Date & Time</th>
                  <th className="text-left px-5 py-3 font-semibold">Dur.</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Volunteer</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, i) => (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    <td className="px-3 py-3">
                      <input type="checkbox" className="w-4 h-4 accent-green-700"
                        checked={selected.has(b.id)}
                        onChange={() => toggleSelect(b.id)}
                        data-testid={`select-booking-${b.id}`} />
                    </td>
                    <td className="px-5 py-3 font-medium text-slate-800">{b.requester_name}</td>
                    <td className="px-5 py-3 text-slate-500">
                      <div>{b.requester_email}</div>
                      <div className="text-xs">{b.requester_phone}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{b.booking_dt_formatted}</td>
                    <td className="px-5 py-3 text-slate-600">{b.duration_hours}h</td>
                    <td className="px-5 py-3"><StatusBadge status={b.status} /></td>
                    <td className="px-5 py-3 text-slate-500">
                      {b.bike_choice === "Trishaw and Wheelchair Bike" ? (
                        <span className="text-xs leading-relaxed">
                          {[
                            b.trishaw_volunteer?.status === "confirmed" ? `T: ${b.trishaw_volunteer.name}` : null,
                            b.wheelchair_volunteer?.status === "confirmed" ? `W: ${b.wheelchair_volunteer.name}` : null,
                          ].filter(Boolean).join(" / ") || <span className="text-slate-300">—</span>}
                        </span>
                      ) : (
                        b.claimed_by_name || <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex gap-2 justify-end items-center">
                        {b.status === "pending" && (
                          <div className="flex items-center gap-1">
                            {b.volunteer_reminder_sent_at && (
                              <span 
                                className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200"
                                title={`Reminder sent ${new Date(b.volunteer_reminder_sent_at).toLocaleString("en-IE")}${b.volunteer_reminder_count > 1 ? ` (${b.volunteer_reminder_count}x)` : ''}`}
                              >
                                <Check className="w-3 h-3" />
                                {b.volunteer_reminder_count > 1 ? `Sent ${b.volunteer_reminder_count}x` : 'Sent'}
                              </span>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleResend(b.id)} 
                              disabled={resending === b.id} 
                              data-testid={`resend-volunteers-${b.id}`} 
                              title={b.volunteer_reminder_sent_at ? "Send another reminder" : "Send volunteer notifications"}
                              className={b.volunteer_reminder_sent_at 
                                ? "border-green-200 text-green-600 hover:bg-green-50" 
                                : "border-blue-200 text-blue-600 hover:bg-blue-50"
                              }
                            >
                              {resending === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setViewBooking(b)} data-testid={`view-booking-${b.id}`} title="View full details" className="border-slate-200 text-slate-600 hover:bg-slate-50">
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        {b.status !== "cancelled" && (
                          <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`edit-booking-${b.id}`} className="border-green-200 text-green-700 hover:bg-green-50">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {b.status === "cancelled" ? (
                          <Button size="sm" variant="outline" onClick={() => handlePermanentDelete(b)} disabled={deleting === b.id} data-testid={`permanent-delete-${b.id}`} title="Permanently delete (removes from statistics)" className="border-red-300 text-red-700 hover:bg-red-100">
                            {deleting === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleDelete(b)} disabled={deleting === b.id} data-testid={`delete-booking-${b.id}`} title="Cancel booking" className="border-red-200 text-red-600 hover:bg-red-50">
                            {deleting === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── NEW BOOKING DIALOG ── */}
      <Dialog open={showNew} onOpenChange={(o) => { if (!o) { setShowNew(false); setNewForm(BLANK_NEW); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-booking-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }} className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-green-700" /> New Booking (Phone / Walk-in)
            </DialogTitle>
            <p className="text-slate-500 text-sm">Create a booking directly without the online form. No 36-hour restriction.</p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Requester details */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Full Name *</Label>
                <Input data-testid="new-requester-name" placeholder="Requester's full name" value={newForm.requester_name}
                  onChange={(e) => setNewForm({ ...newForm, requester_name: e.target.value })}
                  className="h-10 bg-slate-50 border-slate-200" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-600 font-medium mb-1.5 block">Email *</Label>
                  <Input data-testid="new-requester-email" type="email" placeholder="email@example.com" value={newForm.requester_email}
                    onChange={(e) => setNewForm({ ...newForm, requester_email: e.target.value })}
                    className="h-10 bg-slate-50 border-slate-200" />
                </div>
                <div>
                  <Label className="text-slate-600 font-medium mb-1.5 block">Phone</Label>
                  <Input data-testid="new-requester-phone" placeholder="Phone number" value={newForm.requester_phone}
                    onChange={(e) => setNewForm({ ...newForm, requester_phone: e.target.value })}
                    className="h-10 bg-slate-50 border-slate-200" />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Date & Time *</Label>
                <p className="text-xs text-slate-400 mb-1">Enter in system timezone (Europe/Dublin)</p>
                <Input type="datetime-local" data-testid="new-datetime-input"
                  value={newForm.booking_datetime_iso}
                  min={localNow()}
                  onChange={(e) => setNewForm({ ...newForm, booking_datetime_iso: e.target.value })}
                  className="h-10 bg-slate-50 border-slate-200" />
                {(() => {
                  const conflict = findConflict(newForm.booking_datetime_iso, newForm.duration_hours, bookings);
                  return conflict ? (
                    <p className="text-xs text-amber-600 mt-1 font-medium" data-testid="datetime-conflict-warning">
                      Conflicts with existing booking on {new Date(conflict.booking_datetime_iso).toLocaleString("en-IE", { dateStyle: "medium", timeStyle: "short" })} ({conflict.requester_name})
                    </p>
                  ) : null;
                })()}
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Duration (hours)</Label>
                <Input type="number" min="0.5" max="8" step="0.5" data-testid="new-duration-input"
                  value={newForm.duration_hours}
                  onChange={(e) => setNewForm({ ...newForm, duration_hours: e.target.value })}
                  className="h-10 bg-slate-50 border-slate-200" />
              </div>
            </div>

            {/* Meeting Point */}
            <div>
              <Label className="text-slate-600 font-medium mb-1.5 block">Meeting Point</Label>
              <Select value={newForm.meeting_point || "__none__"} onValueChange={(v) => setNewForm({ ...newForm, meeting_point: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="new-meeting-point-select" className="h-10 bg-slate-50 border-slate-200">
                  <SelectValue placeholder="— Select meeting point —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Select meeting point —</SelectItem>
                  {MEETING_POINTS.map((mp) => (
                    <SelectItem key={mp} value={mp}>{mp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bike Type */}
            <div>
              <Label className="text-slate-600 font-medium mb-1.5 block">Bike Type *</Label>
              <Select value={newForm.bike_choice || "Trishaw Only"} onValueChange={handleBikeChoiceChange}>
                <SelectTrigger data-testid="new-bike-type-select" className="h-10 bg-slate-50 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Trishaw Only">Trishaw</SelectItem>
                  <SelectItem value="Wheelchair Bike Only">Wheelchair Bike</SelectItem>
                  <SelectItem value="Trishaw and Wheelchair Bike">Trishaw and Wheelchair Bike</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">For "Wheelchair Bike", only licensed volunteers receive notification emails.</p>
            </div>

            {/* Status */}
            <div>
              <Label className="text-slate-600 font-medium mb-1.5 block">Status</Label>
              <Select value={newForm.status} onValueChange={(v) => setNewForm({ ...newForm, status: v })}>
                <SelectTrigger data-testid="new-status-select" className="h-10 bg-slate-50 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Time Flexibility (optional) */}
            <div>
              <Label className="text-slate-600 font-medium mb-1.5 block" htmlFor="new-time-flex">Time Flexibility (optional)</Label>
              <textarea id="new-time-flex" data-testid="new-time-flexibility"
                value={newForm.time_flexibility || ""}
                onChange={(e) => setNewForm({ ...newForm, time_flexibility: e.target.value })}
                placeholder='e.g. "Flexible between 11am and 1pm" — leave blank if fixed time'
                rows={2}
                className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:border-green-600 focus:bg-white" />
              <p className="text-xs text-slate-400 mt-1">If filled and other bookings exist that day, volunteers see a same-day availability snippet in their notification email.</p>
            </div>

            {/* Volunteer dropdown(s) */}
            {newForm.bike_choice === "Trishaw and Wheelchair Bike" ? (
              <>
                <div>
                  <Label className="text-slate-600 font-medium mb-1.5 block">Trishaw Volunteer</Label>
                  <Select value={newForm.trishaw_volunteer_id || "__none__"} onValueChange={(v) => handleDualVolunteerSelect("trishaw", v)}>
                    <SelectTrigger data-testid="new-trishaw-volunteer-select" className="h-10 bg-slate-50 border-slate-200">
                      <SelectValue placeholder="— No volunteer assigned —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— No volunteer assigned —</SelectItem>
                      {volunteers.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name} ({v.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newForm.trishaw_volunteer_name && (
                    <p className="text-xs text-green-700 mt-1">Assigned: {newForm.trishaw_volunteer_name}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-600 font-medium mb-1.5 block">Wheelchair Bike Volunteer</Label>
                  <Select value={newForm.wheelchair_volunteer_id || "__none__"} onValueChange={(v) => handleDualVolunteerSelect("wheelchair", v)}>
                    <SelectTrigger data-testid="new-wheelchair-volunteer-select" className="h-10 bg-slate-50 border-slate-200">
                      <SelectValue placeholder="— No volunteer assigned —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— No volunteer assigned —</SelectItem>
                      {volunteers.filter((v) => v.wheelchair_license).map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name} ({v.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">Only wheelchair-licensed volunteers can be assigned.</p>
                  {newForm.wheelchair_volunteer_name && (
                    <p className="text-xs text-green-700 mt-1">Assigned: {newForm.wheelchair_volunteer_name}</p>
                  )}
                </div>
                <p className="text-xs text-slate-500 -mt-1">
                  Assigning <strong>both</strong> volunteers will confirm the booking and email the requester the full confirmation. Otherwise the remaining bike(s) stay pending and volunteers are notified as usual.
                </p>
              </>
            ) : (
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Assign Volunteer</Label>
                <Select value={newForm.volunteer_id || "__none__"} onValueChange={handleVolunteerSelect}>
                  <SelectTrigger data-testid="new-volunteer-select" className="h-10 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="— No volunteer assigned —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— No volunteer assigned —</SelectItem>
                    {volunteers.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name} ({v.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newForm.volunteer_name && (
                  <p className="text-xs text-green-700 mt-1">Assigned: {newForm.volunteer_name}</p>
                )}
              </div>
            )}

            {/* Send confirmation toggle — confirmed + volunteer assigned */}
            {newForm.status === "confirmed" && newForm.volunteer_name && (
              <div className="flex items-center gap-3 bg-green-50 rounded-xl p-3 border border-green-100">
                <Switch
                  id="send-confirm"
                  data-testid="send-confirmation-toggle"
                  checked={newForm.send_confirmation}
                  onCheckedChange={(v) => setNewForm({ ...newForm, send_confirmation: v })}
                />
                <Label htmlFor="send-confirm" className="text-slate-700 text-sm cursor-pointer">
                  Send confirmation email to requester
                </Label>
              </div>
            )}

            {/* Send notifications toggle — pending + no volunteer */}
            {newForm.status === "pending" && !newForm.volunteer_name && (
              <div className="flex items-center gap-3 bg-amber-50 rounded-xl p-3 border border-amber-100">
                <Switch
                  id="send-notifications"
                  data-testid="send-notifications-toggle"
                  checked={newForm.send_notifications}
                  onCheckedChange={(v) => setNewForm({ ...newForm, send_notifications: v })}
                />
                <Label htmlFor="send-notifications" className="text-slate-700 text-sm cursor-pointer">
                  Send booking received email to requester + volunteer notification emails
                </Label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNew(false); setNewForm(BLANK_NEW); }}>Cancel</Button>
            <Button onClick={handleCreate}
              disabled={creating || !!findConflict(newForm.booking_datetime_iso, newForm.duration_hours, bookings)}
              data-testid="create-booking-btn"
              className="bg-[#15803d] hover:bg-green-800 text-white">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {creating ? "Creating..." : "Create Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── EDIT DIALOG ── */}
      <Dialog open={!!editBooking} onOpenChange={(o) => !o && setEditBooking(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-booking-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Edit Booking</DialogTitle>
          </DialogHeader>
          {editBooking && (
            <div className="space-y-4 py-2">

              {/* Requester Details */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Requester Details</p>
                <div className="space-y-2">
                  <Input data-testid="edit-requester-name-input" placeholder="Full name"
                    value={editForm.requester_name}
                    onChange={(e) => setEditForm({ ...editForm, requester_name: e.target.value })}
                    className="h-10 bg-slate-50 border-slate-200 text-sm" />
                  <Input data-testid="edit-requester-email-input" type="email" placeholder="Email address"
                    value={editForm.requester_email}
                    onChange={(e) => setEditForm({ ...editForm, requester_email: e.target.value })}
                    className="h-10 bg-slate-50 border-slate-200 text-sm" />
                  <Input data-testid="edit-requester-phone-input" type="tel" placeholder="Phone number"
                    value={editForm.requester_phone}
                    onChange={(e) => setEditForm({ ...editForm, requester_phone: e.target.value })}
                    className="h-10 bg-slate-50 border-slate-200 text-sm" />
                </div>
              </div>

              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Date & Time</Label>
                <p className="text-xs text-slate-400 mb-1">Enter in system timezone (Europe/Dublin)</p>
                <Input type="datetime-local" data-testid="edit-datetime-input" value={editForm.booking_datetime_iso}
                  onChange={(e) => setEditForm({ ...editForm, booking_datetime_iso: e.target.value })}
                  className="h-11 bg-slate-50 border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Duration (hours)</Label>
                <Input type="number" min="0.5" max="8" step="0.5" data-testid="edit-duration-input"
                  value={editForm.duration_hours}
                  onChange={(e) => setEditForm({ ...editForm, duration_hours: e.target.value })}
                  className="h-11 bg-slate-50 border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-status-select" className="h-11 bg-slate-50 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block">Meeting Point</Label>
                <Select value={editForm.meeting_point || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, meeting_point: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-meeting-point-select" className="h-11 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="— Not set —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Not set —</SelectItem>
                    {MEETING_POINTS.map((mp) => (
                      <SelectItem key={mp} value={mp}>{mp}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editBooking?.bike_choice === "Trishaw and Wheelchair Bike" ? (
                <>
                  <div>
                    <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1.5 flex items-center gap-1.5"><Bike className="w-3.5 h-3.5" /> Trishaw Volunteer</p>
                    <div className="space-y-2">
                      <Select value={volunteers.find((v) => v.name === editForm.trishaw_volunteer_name)?.id || "__none__"}
                        onValueChange={(vid) => {
                          const v = volunteers.find((x) => x.id === vid);
                          setEditForm({ ...editForm, trishaw_volunteer_name: v?.name || "", trishaw_volunteer_email: v?.email || "", trishaw_volunteer_phone: v?.phone || "" });
                        }}>
                        <SelectTrigger data-testid="edit-trishaw-volunteer-select" className="h-11 bg-slate-50 border-slate-200">
                          <SelectValue placeholder="— No volunteer —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— No volunteer assigned —</SelectItem>
                          {volunteers.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.phone ? ` (${v.phone})` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1.5 flex items-center gap-1.5"><Bike className="w-3.5 h-3.5" /> Wheelchair Bike Volunteer</p>
                    <div className="space-y-2">
                      <Select value={volunteers.find((v) => v.name === editForm.wheelchair_volunteer_name)?.id || "__none__"}
                        onValueChange={(vid) => {
                          const v = volunteers.find((x) => x.id === vid);
                          setEditForm({ ...editForm, wheelchair_volunteer_name: v?.name || "", wheelchair_volunteer_email: v?.email || "", wheelchair_volunteer_phone: v?.phone || "" });
                        }}>
                        <SelectTrigger data-testid="edit-wheelchair-volunteer-select" className="h-11 bg-slate-50 border-slate-200">
                          <SelectValue placeholder="— No volunteer —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— No volunteer assigned —</SelectItem>
                          {volunteers.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.phone ? ` (${v.phone})` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <Label className="text-slate-600 font-medium mb-1.5 block">Assign Volunteer</Label>
                  <Select
                    value={volunteers.find((v) => v.name === editForm.volunteer_name)?.id || "__none__"}
                    onValueChange={handleEditVolunteerSelect}
                  >
                    <SelectTrigger data-testid="edit-volunteer-select" className="h-11 bg-slate-50 border-slate-200">
                      <SelectValue placeholder="— No volunteer —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— No volunteer assigned —</SelectItem>
                      {volunteers.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name}{v.phone ? ` (${v.phone})` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editForm.volunteer_phone && (
                    <p className="text-xs text-green-700 mt-1">Phone: {editForm.volunteer_phone}</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBooking(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-booking-btn" className="bg-[#15803d] hover:bg-green-800 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ── VIEW BOOKING DIALOG ── */}
      <Dialog open={!!viewBooking} onOpenChange={(o) => !o && setViewBooking(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="view-booking-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }} className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-slate-600" /> Booking Details
            </DialogTitle>
          </DialogHeader>
          {viewBooking && (
            <div className="space-y-4 py-1 text-sm">

              {/* Requester */}
              <section>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Requester</p>
                <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100">
                  <Row label="Name" value={viewBooking.requester_name} testid="view-requester-name" />
                  <Row label="Email" value={viewBooking.requester_email} testid="view-requester-email" />
                  <Row label="Phone" value={viewBooking.requester_phone} testid="view-requester-phone" />
                </div>
              </section>

              {/* Booking */}
              <section>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Booking</p>
                <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100">
                  <Row label="Date & Time" value={viewBooking.booking_dt_formatted} testid="view-datetime" />
                  {viewBooking.original_booking_datetime_iso && (
                    <Row label="Original Request" value={viewBooking.original_booking_datetime_iso ? new Date(viewBooking.original_booking_datetime_iso).toLocaleString("en-IE") : "—"} note="Time was adjusted by volunteer" testid="view-original-dt" />
                  )}
                  <Row label="Duration" value={`${viewBooking.duration_hours} hour(s)`} testid="view-duration" />
                  <Row label="Status" value={<StatusBadge status={viewBooking.status} />} testid="view-status" />
                </div>
              </section>

              {/* Cancellation Details (if cancelled) */}
              {viewBooking.status === "cancelled" && (
                <section>
                  <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">Cancellation Details</p>
                  <div className="bg-red-50 rounded-xl border border-red-100 divide-y divide-red-100">
                    {viewBooking.cancellation_reason && (
                      <Row label="Reason" value={viewBooking.cancellation_reason} testid="view-cancel-reason" />
                    )}
                    {viewBooking.cancelled_at && (
                      <Row label="Cancelled At" value={new Date(viewBooking.cancelled_at).toLocaleString("en-IE")} testid="view-cancelled-at" />
                    )}
                    {viewBooking.cancelled_by && (
                      <Row label="Cancelled By" value={viewBooking.cancelled_by} testid="view-cancelled-by" />
                    )}
                  </div>
                </section>
              )}

              {/* Volunteer */}
              <section>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Volunteer</p>
                <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100">
                  <Row label="Name" value={viewBooking.claimed_by_name || "—"} testid="view-volunteer-name" />
                  <Row label="Email" value={viewBooking.claimed_by_email || "—"} testid="view-volunteer-email" />
                </div>
              </section>

              {/* Form details */}
              <section>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Form Details</p>
                <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100">
                  {viewBooking.is_group_booking && (
                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5"><Bike className="w-3.5 h-3.5" /> Booking Type</span>
                      <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded-full">Group Booking</span>
                    </div>
                  )}
                  <Row label="Meeting Point" value={viewBooking.meeting_point || "—"} testid="view-meeting-point" />
                  <Row label="Bike Choice" value={viewBooking.bike_choice || "Trishaw Only"} testid="view-bike-choice" />
                  {!viewBooking.is_group_booking && (
                    <>
                      <Row label="Time Flexibility" value={viewBooking.time_flexibility || "—"} testid="view-time-flexibility" />
                      <Row label="Extra Info" value={viewBooking.extra_info || "—"} testid="view-extra-info" />
                    </>
                  )}
                </div>
              </section>

              {/* Dual-bike volunteer status */}
              {viewBooking.is_group_booking && viewBooking.bike_choice === "Trishaw and Wheelchair Bike" && (
                <section>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Volunteer Assignments</p>
                  <div className="space-y-2">
                    {[
                      { key: "trishaw", label: "Trishaw", vol: viewBooking.trishaw_volunteer },
                      { key: "wheelchair", label: "Wheelchair Bike", vol: viewBooking.wheelchair_volunteer },
                    ].map(({ key, label, vol }) => (
                      <div key={key} className={`rounded-xl border px-4 py-3 flex items-center justify-between ${
                        vol?.status === "confirmed" ? "border-green-200 bg-green-50" :
                        vol?.status === "cancelled" ? "border-red-100 bg-red-50" :
                        "border-amber-200 bg-amber-50"
                      }`}>
                        <div>
                          <p className="text-xs font-semibold text-slate-600">{label}</p>
                          {vol?.status === "confirmed" && <p className="text-sm font-semibold text-green-800">{vol.name} — {vol.phone || "no phone"}</p>}
                          {vol?.status === "pending" && <p className="text-sm text-amber-700 italic">Pending — no volunteer yet</p>}
                          {vol?.status === "cancelled" && <p className="text-sm text-red-600">Cancelled</p>}
                        </div>
                        {vol?.status === "confirmed" && (
                          <button
                            data-testid={`cancel-${key}-btn`}
                            onClick={() => handleCancelBike(viewBooking.id, key)}
                            disabled={cancellingBike === key}
                            className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 border border-red-200 bg-white hover:bg-red-50 px-2.5 py-1 rounded-full transition-colors font-medium"
                          >
                            {cancellingBike === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                            Cancel slot
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* WhatsApp Snippet */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5 text-green-600" /> WhatsApp Snippet
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopySnippet(viewBooking)}
                      data-testid="copy-whatsapp-snippet-btn"
                      title="Copy to clipboard"
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-green-200 text-green-700 bg-green-50 hover:bg-green-100 transition-colors font-medium"
                    >
                      {snippetCopied
                        ? <><Check className="w-3.5 h-3.5" /> Copied!</>
                        : <><ClipboardCopy className="w-3.5 h-3.5" /> Copy</>}
                    </button>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(buildWhatsAppSnippet(viewBooking))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="open-whatsapp-btn"
                      title="Open in WhatsApp"
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#25d366] text-[#128c4a] bg-[#e9fbe9] hover:bg-[#d1f7dd] transition-colors font-medium"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  </div>
                </div>
                <pre
                  data-testid="whatsapp-snippet-text"
                  className="bg-[#e9fbe9] border border-green-100 rounded-xl px-4 py-3 text-xs text-slate-700 font-mono whitespace-pre-wrap leading-relaxed"
                >
                  {buildWhatsAppSnippet(viewBooking)}
                </pre>
              </section>

              {/* Meta */}
              <section>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Submitted</p>
                <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100">
                  <Row label="Created" value={viewBooking.created_at ? new Date(viewBooking.created_at).toLocaleString("en-IE") : "—"} testid="view-created-at" />
                  {viewBooking.confirmed_at && (
                    <Row label="Confirmed" value={new Date(viewBooking.confirmed_at).toLocaleString("en-IE")} testid="view-confirmed-at" />
                  )}
                </div>
              </section>

              {/* Claim Link Engagement */}
              <section>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Claim Link Clicks</p>
                <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100">
                  <Row label="Total clicks" value={viewBooking.claim_link_clicks || 0} testid="view-claim-clicks" />
                </div>
              </section>

            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewBooking(null)}>Close</Button>
            <Button onClick={() => { setViewBooking(null); openEdit(viewBooking); }} data-testid="view-to-edit-btn"
              className="bg-[#15803d] hover:bg-green-800 text-white gap-2">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dual-bike cancellation dialog ── */}
      <Dialog open={!!bikeDeleteModal} onOpenChange={(o) => { if (!o) setBikeDeleteModal(null); }}>
        <DialogContent className="max-w-md" data-testid="bike-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Group Booking</DialogTitle>
            <DialogDescription>
              This is a group booking. Which bike(s) would you like to cancel?
              Emails will be sent to the affected volunteer(s) and the requester.
            </DialogDescription>
          </DialogHeader>
          {bikeDeleteModal && (() => {
            const bk = bikeDeleteModal.booking;
            const tv = bk.trishaw_volunteer || {};
            const wv = bk.wheelchair_volunteer || {};
            const tActive = tv.status !== "cancelled";
            const wActive = wv.status !== "cancelled";
            const bothAlreadyCancelled = !tActive && !wActive;
            const options = [
              { key: "trishaw",     label: "Cancel Trishaw Only",         disabled: !tActive,              note: tActive ? (tv.name || "No volunteer assigned") : "Already cancelled" },
              { key: "wheelchair",  label: "Cancel Wheelchair Bike Only", disabled: !wActive,              note: wActive ? (wv.name || "No volunteer assigned") : "Already cancelled" },
              { key: "both",        label: "Cancel Both Bikes",           disabled: bothAlreadyCancelled,  note: "Cancels both and deletes the booking", danger: true },
            ];
            return (
              <div className="space-y-2 py-2">
                {options.map(({ key, label, disabled, note, danger }) => (
                  <button key={key}
                    data-testid={`bike-cancel-${key}`}
                    disabled={disabled || !!bikeCancelling}
                    onClick={() => handleBikeCancel(bk.id, key)}
                    className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-all ${
                      disabled
                        ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed"
                        : danger
                        ? "border-red-200 bg-red-50 hover:bg-red-100 text-red-700 cursor-pointer"
                        : "border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800 cursor-pointer"
                    }`}
                  >
                    <div>
                      <p className="font-semibold text-sm">{label}</p>
                      <p className="text-xs mt-0.5 opacity-75">{note}</p>
                    </div>
                    {bikeCancelling === key && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
                  </button>
                ))}
                {bothAlreadyCancelled && (
                  <button
                    data-testid="bike-cancel-delete-all"
                    disabled={!!bikeCancelling}
                    onClick={() => handleBikeCancel(bk.id, "both")}
                    className="w-full flex items-center justify-between rounded-xl border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 px-4 py-3 text-left transition-all cursor-pointer mt-1"
                  >
                    <div>
                      <p className="font-semibold text-sm">Delete Booking Entirely</p>
                      <p className="text-xs mt-0.5 opacity-75">Both bikes already cancelled — removes from list, no emails sent</p>
                    </div>
                    {bikeCancelling === "both" && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
                  </button>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBikeDeleteModal(null)} disabled={!!bikeCancelling}
              className="rounded-full border-slate-200 text-slate-600">
              Keep Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Modal */}
      <Dialog open={!!cancelModal} onOpenChange={(o) => { if (!o) { setCancelModal(null); setCancelReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Cancel Booking
            </DialogTitle>
            <DialogDescription>
              This will cancel the booking and move it to the Cancelled tab for record keeping.
            </DialogDescription>
          </DialogHeader>
          
          {cancelModal && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-800">{cancelModal.booking.requester_name}</p>
                <p className="text-slate-600">{cancelModal.booking.label} at {cancelModal.booking.time_label}</p>
              </div>
              
              <div>
                <Label className="text-slate-700 font-medium mb-2 block">
                  Cancellation Reason <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <textarea
                  data-testid="cancel-reason-input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g., Requester called to cancel, Weather conditions, etc."
                  className="w-full h-24 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  maxLength={500}
                />
                <p className="text-xs text-slate-400 mt-1">{cancelReason.length}/500 characters</p>
              </div>
              
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium text-amber-800">Send cancellation emails</p>
                  <p className="text-xs text-amber-600">Notify requester and assigned volunteer(s)</p>
                </div>
                <Switch
                  data-testid="cancel-send-emails-switch"
                  checked={sendCancelNotifications}
                  onCheckedChange={setSendCancelNotifications}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => { setCancelModal(null); setCancelReason(""); }}
              className="rounded-full border-slate-200"
            >
              Keep Booking
            </Button>
            <Button 
              data-testid="confirm-cancel-booking-btn"
              onClick={doCancelBooking}
              disabled={deleting === cancelModal?.booking?.id}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting === cancelModal?.booking?.id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Cancel Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, note, testid }) {
  return (
    <div className="flex items-start gap-3 px-4 py-2.5">
      <span className="text-slate-500 font-medium w-32 shrink-0 text-xs pt-0.5">{label}</span>
      <span className="text-slate-800 flex-1 text-xs" data-testid={testid}>{value}</span>
      {note && <span className="text-amber-600 text-xs italic ml-1">{note}</span>}
    </div>
  );
}
